// src/services/onchainInfoFi.js
// Lightweight viem helpers to read/write InfoFi on-chain state without relying on DB.

import {
  createPublicClient,
  encodeFunctionData,
  getAddress,
  http,
  keccak256,
  encodePacked,
  parseUnits,
} from "viem";
import {
  InfoFiMarketFactoryAbi,
  InfoFiPriceOracleAbi,
  InfoFiMarketAbi,
  RaffleAbi,
  ERC20Abi,
} from "@/utils/abis";
import { getNetworkByKey, getDefaultNetworkKey } from "@/config/networks";
import { getContractAddresses } from "@/config/contracts";
import {
  queryLogsInChunks,
  estimateBlockFromTimestamp,
} from "@/utils/blockRangeQuery";

// Build an HTTP public client. Event subscriptions used to optionally
// upgrade to a WebSocket transport, but the WS path created log filters
// (eth_newFilter) which the project no longer uses for any subscription —
// callers poll eth_getLogs with locally-tracked block cursors instead.
function buildClients(networkKey) {
  const chain = getNetworkByKey(networkKey);
  const transportHttp = http(chain.rpcUrl);
  const publicClient = createPublicClient({
    chain: { id: chain.id },
    transport: transportHttp,
  });
  return { publicClient };
}

const SUBSCRIBE_POLL_MS = 12_000;

// Shared poll-based event subscription helper. Returns an unsubscribe
// function. Seeds the cursor at the current block so we don't replay
// history — callers that need backfill should do a one-shot
// queryLogsInChunks first and then subscribe.
function pollLogs({ client, address, abi, eventName, onEvent, pollMs = SUBSCRIBE_POLL_MS }) {
  if (!client || !address || !eventName) return () => {};
  const event = Array.isArray(abi)
    ? abi.find((item) => item?.type === "event" && item?.name === eventName)
    : null;
  if (!event) return () => {};

  let cancelled = false;
  let timeoutId = null;
  let cursor = null;

  const tick = async () => {
    if (cancelled) return;
    try {
      const tip = await client.getBlockNumber();
      if (cursor == null) {
        cursor = tip;
      } else if (tip > cursor) {
        const logs = await client.getLogs({
          address,
          event,
          fromBlock: cursor + 1n,
          toBlock: tip,
        });
        for (const log of logs) {
          if (cancelled) break;
          try { onEvent?.(log); } catch { /* ignore listener errors */ }
        }
        cursor = tip;
      }
    } catch {
      // Swallow transient RPC errors — next tick retries.
    } finally {
      if (!cancelled) timeoutId = setTimeout(tick, pollMs);
    }
  };

  tick();

  return () => {
    cancelled = true;
    if (timeoutId) clearTimeout(timeoutId);
  };
}

// Read full bet info including claimed/payout, preferring explicit prediction overload
export async function readBetFull({
  marketId,
  account,
  prediction,
  networkKey = getDefaultNetworkKey(),
  contractAddress, // Required parameter, no default
}) {
  if (!contractAddress) {
    throw new Error("Contract address is required");
  }

  const { publicClient } = buildClients(networkKey);
  const idU256 = toUint256Id(marketId);

  // Try explicit overload when prediction is provided
  if (typeof prediction === "boolean") {
    try {
      const bet = await publicClient.readContract({
        address: contractAddress,
        abi: InfoFiMarketAbi,
        functionName: "getBet",
        args: [idU256, getAddress(account), prediction],
      });
      return {
        prediction: bet.prediction ?? prediction,
        amount: BigInt(bet.amount ?? 0),
        claimed: Boolean(bet.claimed),
        payout: BigInt(bet.payout ?? 0),
      };
    } catch (error) {
      // Error in readBet with prediction, falling back to full scan
      // Continue to full scan on error
    }
  }

  // Fall back to checking both sides if prediction not provided or fails
  const [yesBet, noBet] = await Promise.all([
    publicClient
      .readContract({
        address: contractAddress,
        abi: InfoFiMarketAbi,
        functionName: "getBet",
        args: [idU256, getAddress(account), true],
      })
      .then((bet) => ({
        prediction: true,
        amount: BigInt(bet.amount ?? 0),
        claimed: Boolean(bet.claimed),
        payout: BigInt(bet.payout ?? 0),
      }))
      .catch(() => ({
        prediction: true,
        amount: 0n,
        claimed: false,
        payout: 0n,
      })),
    publicClient
      .readContract({
        address: contractAddress,
        abi: InfoFiMarketAbi,
        functionName: "getBet",
        args: [idU256, getAddress(account), false],
      })
      .then((bet) => ({
        prediction: false,
        amount: BigInt(bet.amount ?? 0),
        claimed: Boolean(bet.claimed),
        payout: BigInt(bet.payout ?? 0),
      }))
      .catch(() => ({
        prediction: false,
        amount: 0n,
        claimed: false,
        payout: 0n,
      })),
  ]);

  // Return the non-zero position if any
  if (yesBet.amount > 0n) return yesBet;
  if (noBet.amount > 0n) return noBet;
  return { prediction: null, amount: 0n, claimed: false, payout: 0n };
}

// Removed enumerateAllMarkets - now using backend API for market data

// Helpers to normalize marketId into both candidate shapes
function toUint256Id(marketId) {
  try {
    if (typeof marketId === "bigint") return marketId;
    if (typeof marketId === "number") return BigInt(marketId);
    if (typeof marketId === "string") {
      if (marketId.startsWith("0x")) return BigInt(marketId); // allow hex -> bigint
      return BigInt(marketId);
    }
  } catch (_) {
    /* fallthrough */
  }
  return 0n;
}

function toBytes32Id(marketId) {
  try {
    if (
      typeof marketId === "string" &&
      marketId.startsWith("0x") &&
      marketId.length === 66
    )
      return marketId;
    const bn = toUint256Id(marketId);
    return `0x${bn.toString(16).padStart(64, "0")}`;
  } catch (_) {
    return "0x".padEnd(66, "0");
  }
}

function getContracts(networkKey) {
  const addrs = getContractAddresses(networkKey);
  return {
    factory: {
      address: addrs.INFOFI_FACTORY,
      abi: InfoFiMarketFactoryAbi,
    },
    oracle: {
      address: addrs.INFOFI_ORACLE,
      abi: InfoFiPriceOracleAbi,
    },
    market: {
      address: addrs.INFOFI_MARKET,
      abi: InfoFiMarketAbi,
    },
  };
}

export async function getSeasonPlayersOnchain({
  seasonId,
  networkKey = getDefaultNetworkKey(),
}) {
  const { publicClient } = buildClients(networkKey);
  const { factory } = getContracts(networkKey);

  // eslint-disable-next-line no-console
  console.log(
    `[getSeasonPlayersOnchain] seasonId=${seasonId}, networkKey=${networkKey}, factory.address=${factory.address}`
  );

  if (!factory.address) {
    // eslint-disable-next-line no-console
    console.warn(
      "[getSeasonPlayersOnchain] No factory address configured, returning empty array"
    );
    return [];
  }
  try {
    const players = await publicClient.readContract({
      address: factory.address,
      abi: factory.abi,
      functionName: "getSeasonPlayers",
      args: [BigInt(seasonId)],
    });
    // eslint-disable-next-line no-console
    console.log(
      `[getSeasonPlayersOnchain] Successfully fetched ${
        players?.length || 0
      } players:`,
      players
    );
    return players || [];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("[getSeasonPlayersOnchain] Error fetching players:", error);
    return [];
  }
}

export async function hasWinnerMarketOnchain({
  seasonId,
  player,
  networkKey = getDefaultNetworkKey(),
}) {
  const { publicClient } = buildClients(networkKey);
  const { factory } = getContracts(networkKey);
  if (!factory.address) throw new Error("INFOFI_FACTORY address missing");
  const created = await publicClient.readContract({
    address: factory.address,
    abi: factory.abi,
    functionName: "hasWinnerMarket",
    args: [BigInt(seasonId), getAddress(player)],
  });
  return created;
}

/**
 * Build a { to, data } call object for creating a winner prediction market.
 * Caller should pass this to executeBatch([call]).
 */
export function buildCreateWinnerPredictionMarketCall({
  seasonId,
  player,
  networkKey = getDefaultNetworkKey(),
}) {
  const { factory } = getContracts(networkKey);
  return {
    to: factory.address,
    data: encodeFunctionData({
      abi: factory.abi,
      functionName: "createWinnerPredictionMarket",
      args: [BigInt(seasonId), getAddress(player)],
    }),
  };
}

// Subscribe to MarketCreated via eth_getLogs polling. Returns an unsub
// function; safe to call before/after the factory address is resolved.
export function subscribeMarketCreated({ networkKey = "TESTNET", onEvent }) {
  const { publicClient } = buildClients(networkKey);
  const { factory } = getContracts(networkKey);
  return pollLogs({
    client: publicClient,
    address: factory.address,
    abi: factory.abi,
    eventName: "MarketCreated",
    onEvent,
  });
}

// Helper to safely convert BigInt to Number for basis points
function bpsToNumber(value) {
  if (value == null) return null;
  if (typeof value === "number") return value;
  if (typeof value === "bigint") {
    const num = Number(value);
    // Sanity check: basis points should be 0-10000
    return num >= 0 && num <= 10000 ? num : null;
  }
  // Try parsing string
  const parsed = Number(value);
  return !Number.isNaN(parsed) && parsed >= 0 && parsed <= 10000
    ? parsed
    : null;
}

// Oracle: read full price struct for a marketId (bytes32)
export async function readOraclePrice({ marketId, networkKey = "TESTNET" }) {
  const { publicClient } = buildClients(networkKey);
  const { oracle } = getContracts(networkKey);
  if (!oracle.address) throw new Error("INFOFI_ORACLE address missing");
  // Try both id shapes
  const idB32 = toBytes32Id(marketId);
  const idU256 = toUint256Id(marketId);
  // Try primary getter first
  try {
    const price = await publicClient.readContract({
      address: oracle.address,
      abi: oracle.abi,
      functionName: "getPrice",
      args: [idU256],
    });
    // Expect struct PriceData { raffleProbabilityBps, marketSentimentBps, hybridPriceBps, lastUpdate, active }
    // Normalize BigInt values to Numbers
    const normalized = {
      raffleProbabilityBps: bpsToNumber(price.raffleProbabilityBps ?? price[0]),
      marketSentimentBps: bpsToNumber(price.marketSentimentBps ?? price[1]),
      hybridPriceBps: bpsToNumber(price.hybridPriceBps ?? price[2]),
      lastUpdate: Number(price.lastUpdate ?? price[3] ?? 0),
      active: Boolean(price.active ?? price[4] ?? false),
    };
    return normalized;
  } catch (_) {
    // Fallback: some oracles expose only getMarketPrice(bytes32) returning hybrid price
    try {
      const hybrid = await publicClient.readContract({
        address: oracle.address,
        abi: oracle.abi,
        functionName: "getMarketPrice",
        args: [idB32],
      });
      return {
        raffleProbabilityBps: null,
        marketSentimentBps: null,
        hybridPriceBps: bpsToNumber(hybrid),
        lastUpdate: 0,
        active: true,
      };
    } catch (e2) {
      // Last try: some implementations might index by uint256
      try {
        const price = await publicClient.readContract({
          address: oracle.address,
          abi: oracle.abi,
          functionName: "getPriceU256", // optional alternative
          args: [idU256],
        });
        const normalized = {
          raffleProbabilityBps: bpsToNumber(
            price.raffleProbabilityBps ?? price[0]
          ),
          marketSentimentBps: bpsToNumber(price.marketSentimentBps ?? price[1]),
          hybridPriceBps: bpsToNumber(price.hybridPriceBps ?? price[2]),
          lastUpdate: Number(price.lastUpdate ?? price[3] ?? 0),
          active: Boolean(price.active ?? price[4] ?? false),
        };
        return normalized;
      } catch (_) {
        /* no-op */
      }
      // Last resort: return an inactive struct
      return {
        raffleProbabilityBps: null,
        marketSentimentBps: null,
        hybridPriceBps: null,
        lastUpdate: 0,
        active: false,
      };
    }
  }
}

// Oracle: subscribe to PriceUpdated via eth_getLogs polling.
export function subscribeOraclePriceUpdated({
  networkKey = getDefaultNetworkKey(),
  onEvent,
}) {
  const { publicClient } = buildClients(networkKey);
  const { oracle } = getContracts(networkKey);
  return pollLogs({
    client: publicClient,
    address: oracle.address,
    abi: oracle.abi,
    eventName: "PriceUpdated",
    onEvent,
  });
}

// Compute bytes32 marketId for WINNER_PREDICTION markets
// Solidity: keccak256(abi.encodePacked(seasonId, player, keccak256("WINNER_PREDICTION")))
export function computeWinnerMarketId({ seasonId, player }) {
  const typeHash = keccak256(encodePacked(["string"], ["WINNER_PREDICTION"]));
  const packed = encodePacked(
    ["uint256", "address", "bytes32"],
    [BigInt(seasonId), getAddress(player), typeHash]
  );
  return keccak256(packed);
}

// Enumerate season winner markets purely from chain
export async function listSeasonWinnerMarkets({
  seasonId,
  networkKey = getDefaultNetworkKey(),
}) {
  // Prefer real market ids from MarketCreated events (uint256), fallback to synthetic if needed
  const byEvents = await listSeasonWinnerMarketsByEvents({
    seasonId,
    networkKey,
  });

  // Enrich markets with oracle probability data
  if (byEvents.length > 0) {
    const marketsWithProbability = await Promise.all(
      byEvents.map(async (market) => {
        try {
          const priceData = await readOraclePrice({
            marketId: market.id,
            networkKey,
          });

          return {
            ...market,
            current_probability: priceData.hybridPriceBps,
            raffle_probability: priceData.raffleProbabilityBps,
            market_sentiment: priceData.marketSentimentBps,
          };
        } catch (_error) {
          // If oracle read fails, return market without probability
          return market;
        }
      })
    );

    return marketsWithProbability;
  }

  // Fallback: derive from players and read uint256 IDs from factory mapping
  const { publicClient } = buildClients(networkKey);
  const { factory } = getContracts(networkKey);
  const players = await getSeasonPlayersOnchain({ seasonId, networkKey });

  const markets = [];
  for (const p of players) {
    try {
      // Read the actual uint256 market ID from the factory mapping
      const marketId = await publicClient.readContract({
        address: factory.address,
        abi: factory.abi,
        functionName: "winnerPredictionMarketIds",
        args: [BigInt(seasonId), getAddress(p)],
      });

      markets.push({
        id: marketId.toString(), // Use uint256 ID
        seasonId: Number(seasonId),
        raffle_id: Number(seasonId),
        player: getAddress(p),
        market_type: "WINNER_PREDICTION",
      });
    } catch (e) {
      // If reading fails, fall back to bytes32 ID
      markets.push({
        id: computeWinnerMarketId({ seasonId, player: p }),
        seasonId: Number(seasonId),
        raffle_id: Number(seasonId),
        player: getAddress(p),
        market_type: "WINNER_PREDICTION",
      });
    }
  }

  return markets;
}

// Helper to get season start block for efficient log queries
async function getSeasonStartBlock({ seasonId, networkKey = "TESTNET" }) {
  const { publicClient } = buildClients(networkKey);
  const addrs = getContractAddresses(networkKey);

  // Try to get season start time from Raffle contract
  try {
    const result = await publicClient.readContract({
      address: addrs.RAFFLE,
      abi: RaffleAbi,
      functionName: "getSeasonDetails",
      args: [BigInt(seasonId)],
    });

    const config = result[0] || result?.config;
    const startTime = Number(config?.startTime || config?.[0] || 0);

    if (startTime > 0) {
      // Estimate block from timestamp using network-specific block time
      const chain = getNetworkByKey(networkKey);
      return await estimateBlockFromTimestamp(
        publicClient,
        startTime,
        chain.avgBlockTime
      );
    }
  } catch (e) {
    // Failed to get season start time, will use fallback
    // Error details: e.message
  }

  // Fallback: use network-specific lookback blocks
  const chain = getNetworkByKey(networkKey);
  const currentBlock = await publicClient.getBlockNumber();
  const lookbackBlocks = chain.lookbackBlocks;
  return currentBlock > lookbackBlocks ? currentBlock - lookbackBlocks : 0n;
}

// Retrieve winner markets via factory events (real uint256 ids)
export async function listSeasonWinnerMarketsByEvents({
  seasonId,
  networkKey = getDefaultNetworkKey(),
}) {
  const { publicClient } = buildClients(networkKey);
  const { factory } = getContracts(networkKey);
  if (!factory.address) throw new Error("INFOFI_FACTORY address missing");

  // Get season start block for efficient querying
  const fromBlock = await getSeasonStartBlock({ seasonId, networkKey });

  // Build event filter
  const eventAbi = InfoFiMarketFactoryAbi.find(
    (e) => e.type === "event" && e.name === "MarketCreated"
  );

  // Use chunked query to avoid RPC limits
  const logs = await queryLogsInChunks(
    publicClient,
    {
      address: factory.address,
      event: {
        name: "MarketCreated",
        type: "event",
        inputs: eventAbi.inputs,
      },
      fromBlock,
      toBlock: "latest",
    },
    10000n // Max 10k blocks per chunk (safe for most RPC providers)
  );

  const out = [];
  for (const log of logs) {
    const args = log.args || {};
    // Some ABIs name it `seasonId`; guard both cases
    const sid = Number(args.seasonId ?? args._seasonId ?? 0);
    if (sid !== Number(seasonId)) continue;
    // Market type filter (we only list winner prediction here)
    const mtype = String(
      args.marketType || args._marketType || "WINNER_PREDICTION"
    );
    if (mtype !== "WINNER_PREDICTION") continue;
    const player =
      args.player ||
      args._player ||
      "0x0000000000000000000000000000000000000000";

    // Extract marketId from event (now emitted in the event)
    let marketId = args.marketId ?? args._marketId;

    // If not in event, read from factory's storage mapping as fallback
    if (!marketId || marketId === 0n || marketId === "0") {
      try {
        marketId = await publicClient.readContract({
          address: factory.address,
          abi: factory.abi,
          functionName: "winnerPredictionMarketIds",
          args: [BigInt(sid), getAddress(player)],
        });
      } catch (e) {
        // Last resort: use 0
        marketId = 0n;
      }
    }

    // Normalize id to a plain decimal string when bigint, otherwise hex string
    let idNorm;
    if (typeof marketId === "bigint") idNorm = marketId.toString();
    else if (typeof marketId === "string") idNorm = marketId;
    else idNorm = String(marketId ?? "0");

    out.push({
      id: idNorm,
      seasonId: sid,
      raffle_id: sid,
      player: getAddress(player),
      market_type: "WINNER_PREDICTION",
    });
  }
  return out;
}

// Read a user's bet position for a given marketId and side
export async function readBet({
  account,
  prediction,
  networkKey = getDefaultNetworkKey(),
  fpmmAddress,
}) {
  const { publicClient } = buildClients(networkKey);

  // FPMM address is required - must be provided from market data
  if (!fpmmAddress) {
    throw new Error(
      "fpmmAddress is required. Market contract address must be provided from market data."
    );
  }

  try {
    const fpmmAbi = [
      {
        type: "function",
        name: "balanceOf",
        inputs: [
          { name: "account", type: "address" },
          { name: "id", type: "uint256" },
        ],
        outputs: [{ name: "", type: "uint256" }],
        stateMutability: "view",
      },
    ];

    // Conditional token IDs: 0 = NO, 1 = YES
    const tokenId = prediction ? 1n : 0n;

    const balance = await publicClient.readContract({
      address: fpmmAddress,
      abi: fpmmAbi,
      functionName: "balanceOf",
      args: [getAddress(account), tokenId],
    });

    return { amount: balance };
  } catch (error) {
    // Contract reverted - likely user has no position or token ID doesn't exist yet
    // This is expected for users who haven't placed bets, so just return 0
    if (error.message?.includes("reverted")) {
      return { amount: 0n };
    }
    // Return empty position
    return { amount: 0n };
  }
}

/**
 * Read user's position in an FPMM market
 * Positions are held as Conditional Tokens (ERC1155), not in a mapping
 * @param {Object} params
 * @param {string} params.seasonId - Season ID
 * @param {string} params.player - Player address
 * @param {string} params.account - User address to check
 * @param {boolean} params.prediction - true for YES, false for NO
 * @param {string} params.networkKey - Network key
 * @returns {Promise<{amount: bigint}>} User's position amount
 */
export async function readFpmmPosition({
  seasonId,
  player,
  account,
  prediction,
  networkKey = getDefaultNetworkKey(),
  fpmmAddress: providedFpmmAddress,
}) {
  const chain = getNetworkByKey(networkKey);
  const publicClient = createPublicClient({
    chain: { id: chain.id },
    transport: http(chain.rpcUrl),
  });
  const addrs = getContractAddresses(networkKey);

  if (!addrs.CONDITIONAL_TOKENS) {
    // CONDITIONAL_TOKENS address not configured
    return { amount: 0n };
  }

  try {
    // Use provided FPMM address from database, or look it up from manager
    let fpmmAddress = providedFpmmAddress;

    if (!fpmmAddress && addrs.INFOFI_FPMM) {
      // Fallback: Get FPMM address for this player/season from manager
      const fpmmManagerAbi = [
        {
          type: "function",
          name: "getMarket",
          inputs: [
            { name: "seasonId", type: "uint256" },
            { name: "player", type: "address" },
          ],
          outputs: [{ name: "", type: "address" }],
          stateMutability: "view",
        },
      ];

      fpmmAddress = await publicClient.readContract({
        address: addrs.INFOFI_FPMM,
        abi: fpmmManagerAbi,
        functionName: "getMarket",
        args: [BigInt(seasonId), getAddress(player)],
      });
    }

    if (
      !fpmmAddress ||
      fpmmAddress === "0x0000000000000000000000000000000000000000"
    ) {
      return { amount: 0n };
    }

    // SimpleFPMM ABI to get position IDs
    const fpmmAbi = [
      {
        type: "function",
        name: "positionIds",
        inputs: [{ name: "", type: "uint256" }],
        outputs: [{ name: "", type: "uint256" }],
        stateMutability: "view",
      },
    ];

    // Get position ID for YES (0) or NO (1)
    const positionId = await publicClient.readContract({
      address: fpmmAddress,
      abi: fpmmAbi,
      functionName: "positionIds",
      args: [prediction ? 0n : 1n],
    });

    // ConditionalTokens ABI for balanceOf
    const conditionalTokensAbi = [
      {
        type: "function",
        name: "balanceOf",
        inputs: [
          { name: "owner", type: "address" },
          { name: "positionId", type: "uint256" },
        ],
        outputs: [{ name: "", type: "uint256" }],
        stateMutability: "view",
      },
    ];

    // Query user's conditional token balance
    const balance = await publicClient.readContract({
      address: addrs.CONDITIONAL_TOKENS,
      abi: conditionalTokensAbi,
      functionName: "balanceOf",
      args: [getAddress(account), positionId],
    });

    return { amount: balance };
  } catch (error) {
    // Error reading FPMM position - return empty position
    return { amount: 0n };
  }
}

// SimpleFPMM ABI fragment for buy + calcBuyAmount (shared by builder and read helpers)
const fpmmBuyAbi = [
  {
    type: "function",
    name: "buy",
    inputs: [
      { name: "buyYes", type: "bool" },
      { name: "amountIn", type: "uint256" },
      { name: "minAmountOut", type: "uint256" },
    ],
    outputs: [{ name: "amountOut", type: "uint256" }],
    stateMutability: "nonpayable",
  },
  {
    type: "function",
    name: "calcBuyAmount",
    inputs: [
      { name: "buyYes", type: "bool" },
      { name: "amountIn", type: "uint256" },
    ],
    outputs: [{ name: "amountOut", type: "uint256" }],
    stateMutability: "view",
  },
];

/**
 * The ERC-20 a market is collateralised in. Each market uses its own season's
 * quote token (the FPMM manager has no platform-wide collateral), so read it
 * off the market rather than from the deployment config.
 */
export async function readMarketCollateral(publicClient, fpmmAddress) {
  const token = await publicClient.readContract({
    address: fpmmAddress,
    abi: [
      {
        type: "function",
        name: "collateralToken",
        inputs: [],
        outputs: [{ name: "", type: "address" }],
        stateMutability: "view",
      },
    ],
    functionName: "collateralToken",
  });
  if (!token || token === "0x0000000000000000000000000000000000000000") {
    throw new Error("Market collateral token unavailable");
  }
  return token;
}

/**
 * Build an array of { to, data } call objects for placing a bet via FPMM.
 * Returns 1 call (buy only) or 2 calls (approve + buy) depending on current allowance.
 * Caller should pass these to executeBatch(calls).
 *
 * @param {Object} params
 * @param {boolean} params.prediction - true for YES, false for NO
 * @param {string|number|bigint} params.amount - collateral amount (the market's quote token, 18 decimals) as human string/number or bigint
 * @param {string} params.account - The user's wallet address (needed for allowance check)
 * @param {string} params.fpmmAddress - FPMM market contract address
 * @param {string} [params.networkKey] - Network key
 * @returns {Promise<Array<{to: string, data: string}>>} Array of call objects for executeBatch
 */
export async function buildPlaceBetCalls({
  prediction,
  amount,
  account,
  fpmmAddress,
  networkKey = getDefaultNetworkKey(),
}) {
  if (!fpmmAddress) {
    throw new Error(
      "fpmmAddress is required. Market contract address must be provided from market data."
    );
  }
  if (fpmmAddress === "0x0000000000000000000000000000000000000000") {
    throw new Error("Invalid FPMM market address (zero address)");
  }
  if (!account) {
    throw new Error("account address is required for allowance check");
  }

  const chain = getNetworkByKey(networkKey);
  const publicClient = createPublicClient({
    chain: { id: chain.id },
    transport: http(chain.rpcUrl),
  });

  const collateral = await readMarketCollateral(publicClient, fpmmAddress);

  const parsed =
    typeof amount === "bigint" ? amount : parseUnits(String(amount ?? "0"), 18);

  // Calculate minimum amount out (allow 2% slippage)
  let minAmountOut = 0n;
  try {
    const expectedOut = await publicClient.readContract({
      address: fpmmAddress,
      abi: fpmmBuyAbi,
      functionName: "calcBuyAmount",
      args: [Boolean(prediction), parsed],
    });
    minAmountOut = (expectedOut * 98n) / 100n; // 2% slippage tolerance
  } catch (_) {
    // If calculation fails, use 0 (no slippage protection)
    minAmountOut = 0n;
  }

  const calls = [];

  // Check current allowance — include approve call only if needed
  const allowance = await publicClient.readContract({
    address: collateral,
    abi: ERC20Abi,
    functionName: "allowance",
    args: [getAddress(account), fpmmAddress],
  });

  if ((allowance ?? 0n) < parsed) {
    calls.push({
      to: collateral,
      data: encodeFunctionData({
        abi: ERC20Abi,
        functionName: "approve",
        args: [fpmmAddress, parsed],
      }),
    });
  }

  // Buy call
  calls.push({
    to: fpmmAddress,
    data: encodeFunctionData({
      abi: fpmmBuyAbi,
      functionName: "buy",
      args: [Boolean(prediction), parsed, minAmountOut],
    }),
  });

  return calls;
}

/**
 * Build a { to, data } call object for claiming a market payout.
 * Caller should pass this to executeBatch([call]).
 *
 * @param {Object} params
 * @param {string|number|bigint} params.marketId - Market ID
 * @param {boolean} [params.prediction] - If provided, uses the two-arg overload
 * @param {string} params.account - The user's wallet address (needed for claim args)
 * @param {string} params.contractAddress - InfoFi market contract address
 */
export function buildClaimPayoutCall({
  marketId,
  prediction,
  account,
  contractAddress,
}) {
  if (!contractAddress) {
    throw new Error("Contract address is required");
  }
  if (!account) {
    throw new Error("account address is required");
  }

  const idB32 = toBytes32Id(marketId);
  const args =
    prediction !== undefined
      ? [idB32, getAddress(account), prediction]
      : [idB32];

  return {
    to: contractAddress,
    data: encodeFunctionData({
      abi: InfoFiMarketAbi,
      functionName: "claimPayout",
      args,
    }),
  };
}

/**
 * Build a { to, data } call object for redeeming conditional tokens after market resolution.
 * Requires an async read to fetch the conditionId from the FPMM contract.
 * Caller should pass this to executeBatch([call]).
 *
 * @param {Object} params
 * @param {string} params.seasonId - Season ID
 * @param {string} params.player - Player address whose market to redeem from
 * @param {string} [params.fpmmAddress] - FPMM market address (looked up if not provided)
 * @param {string} [params.networkKey] - Network key (LOCAL or TESTNET)
 * @returns {Promise<{to: string, data: string}>} Call object for executeBatch
 */
export async function buildRedeemPositionCall({
  seasonId,
  player,
  fpmmAddress: providedFpmmAddress,
  networkKey = getDefaultNetworkKey(),
}) {
  const chain = getNetworkByKey(networkKey);
  const publicClient = createPublicClient({
    chain: { id: chain.id },
    transport: http(chain.rpcUrl),
  });

  const addrs = getContractAddresses(networkKey);
  if (!addrs.CONDITIONAL_TOKENS)
    throw new Error("CONDITIONAL_TOKENS address missing");
  // Use provided FPMM address or look it up
  let fpmmAddress = providedFpmmAddress;

  if (!fpmmAddress) {
    if (!addrs.INFOFI_FPMM) throw new Error("INFOFI_FPMM address missing");

    const fpmmManagerAbi = [
      {
        type: "function",
        name: "getMarket",
        inputs: [
          { name: "seasonId", type: "uint256" },
          { name: "player", type: "address" },
        ],
        outputs: [{ name: "", type: "address" }],
        stateMutability: "view",
      },
    ];

    fpmmAddress = await publicClient.readContract({
      address: addrs.INFOFI_FPMM,
      abi: fpmmManagerAbi,
      functionName: "getMarket",
      args: [BigInt(seasonId), getAddress(player)],
    });
  }

  if (
    !fpmmAddress ||
    fpmmAddress === "0x0000000000000000000000000000000000000000"
  ) {
    throw new Error("No FPMM market exists for this player");
  }

  // Get condition ID from FPMM
  const fpmmConditionAbi = [
    {
      type: "function",
      name: "conditionId",
      inputs: [],
      outputs: [{ name: "", type: "bytes32" }],
      stateMutability: "view",
    },
  ];

  const conditionId = await publicClient.readContract({
    address: fpmmAddress,
    abi: fpmmConditionAbi,
    functionName: "conditionId",
  });

  const collateral = await readMarketCollateral(publicClient, fpmmAddress);

  // ConditionalTokens ABI for redeemPositions
  const conditionalTokensAbi = [
    {
      type: "function",
      name: "redeemPositions",
      inputs: [
        { name: "collateralToken", type: "address" },
        { name: "parentCollectionId", type: "bytes32" },
        { name: "conditionId", type: "bytes32" },
        { name: "indexSets", type: "uint256[]" },
      ],
      outputs: [],
      stateMutability: "nonpayable",
    },
  ];

  // Redeem both YES and NO positions
  // indexSets: [1] = 0b01 (YES), [2] = 0b10 (NO)
  const indexSets = [1, 2];

  return {
    to: addrs.CONDITIONAL_TOKENS,
    data: encodeFunctionData({
      abi: conditionalTokensAbi,
      functionName: "redeemPositions",
      args: [
        collateral, // the market's own collateral (its season's quote token)
        "0x0000000000000000000000000000000000000000000000000000000000000000", // parentCollectionId (empty)
        conditionId, // conditionId
        indexSets, // indexSets [1, 2]
      ],
    }),
  };
}
